import { BrowserModule } from '@angular/platform-browser';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { LOCALE_ID, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from '@angular/cdk/layout';
import { IconsProviderModule } from './icons-provider.module';
import { NgZorroAntdModule, NZ_I18N, en_US } from 'ng-zorro-antd';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';

import localeHr from '@angular/common/locales/hr';
registerLocaleData(localeHr);
// registerLocaleData(en);

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzMessageModule } from 'ng-zorro-antd/message';

import { OrderModule } from 'ngx-order-pipe';
import { ZXingScannerModule } from '@zxing/ngx-scanner';

import { JwtInterceptor, ErrorInterceptor } from './_helpers';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { AssetsComponent } from './assets/assets.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import { LocationsComponent } from './locations/locations.component';
import { EmployeesComponent } from './employees/employees.component';
import { AssetDetailsComponent } from './assets/asset-details/asset-details.component';
import { EmployeeDetailsComponent } from './employees/employee-details/employee-details.component';
import { StopClickPropagationDirective } from '@app/_directives/stop-click-propagation.directive';
import { LocationDetailsComponent } from './location-details/location-details.component';
import { InventoryComponent } from './inventory/inventory.component';
import { SizeDetectorComponent } from './size-detector/size-detector.component';
import { InventoryAssetsComponent } from './inventory-assets/inventory-assets.component';
import { AssetEditorComponent } from './assets/asset-editor/asset-editor.component';
import { DocumentsNeeded } from './_pipes/documents-needed.pipe';
import { AutonumericModule } from '@angularfy/autonumeric';
import { SortDocuments } from './_pipes/sort-documents.pipe';
import { AssetListComponent } from './assets/asset-list/asset-list.component';
import { UserListComponent } from './users/users-list.component';
import { SelectionComponent } from './selection/selection.component';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { InventoryListComponent } from './inventory-list/inventory-list.component';
import { InventoryCreateComponent } from './inventory-create/inventory-create.component';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { ConflictingAssets } from './_pipes/conflicting-assets.pipe';
import { IconDefinition } from '@ant-design/icons-angular';
import { NzIconModule, NZ_ICONS, NZ_ICON_DEFAULT_TWOTONE_COLOR } from 'ng-zorro-antd/icon';

import { 
  DeleteFill, 
  LogoutOutline, 
  MenuUnfoldOutline, 
  MenuFoldOutline ,
  PlusCircleFill,
  PlusCircleOutline,
  SearchOutline,
  FilterFill,
  FileAddOutline,
  FileAddFill,
  FilePdfFill,
  FilePdfOutline,
  EditFill,
  EditOutline,
  EditTwoTone,
  InfoCircleFill,
  InfoCircleOutline,
  ArrowLeftOutline,
  UserOutline,
  UserAddOutline,
  UserDeleteOutline,
  PlusSquareFill,
  PlusSquareOutline,
  PlusSquareTwoTone,
  MinusSquareFill,
  MinusSquareTwoTone,
  MinusSquareOutline,
  EnvironmentTwoTone,
  PlusCircleTwoTone,
  PlusOutline,
  BarcodeOutline,
  CheckOutline,
  CheckCircleFill,
  CheckCircleTwoTone,
  CheckCircleOutline,
  CheckSquareFill,
  CheckSquareOutline,
  CheckSquareTwoTone,
  InboxOutline
} from '@ant-design/icons-angular/icons';
import { SameBarCode } from './_pipes/same-barcode.pipe';
import { SameBarCodeFoundAssets } from './_pipes/same-barcode-found-assets.pipe';
import { InventoryFoundAssetEditorComponent } from './inventory/found-asset-editor/found-asset-editor.component';
import { NotSelectedItems } from './_pipes/not-selected-items.pipe';
import { UsersSelectorComponent } from './selection/users-selector/users-selector.component';
import { DepartmentSelectorComponent } from './selection/department-selector/department-selector.component';
import { EmployeeSelectorComponent } from './selection/employee-selector/employee-selector.component';
import { LocationSelectorComponent } from './selection/location-selector/location-selector.component';
import { AssetGroupSelectorComponent } from './selection/assetGroup-selector/assetGroup-selector.component';
import { InventoryDetailsComponent } from './inventory-details/inventory-details.component';
import { ExcelUploaderComponent } from './excel-uploader/excel-uploader.component';
import { AdminPageComponent } from './admin-page/admin-page.component';
import { FoundAssetsComponent } from './found-assets/found-assets.component';
import { FoundAssetDetailsComponent } from './found-asset-details/found-asset-details.component';
import { SuppliersComponent } from './suppliers/suppliers.component';
import { SupplierEditorComponent } from './suppliers/supplier-editor/supplier-editor.component';
import { SupplierEditorDrawerComponent } from './suppliers/supplier-editor-drawer/supplier-editor-drawer.component';
import { DiscardRecommendationsComponent } from './discard-recommendations/discard-recommendations.component';
import { LegalEntitiesComponent } from './legal-entities/legal-entities.component';
import { LegalEntityEditorComponent } from './legal-entities/legal-entity-editor/legal-entity-editor.component';
import { LegalEntityEditorDrawerComponent } from './legal-entities/legal-entity-editor-drawer/legal-entity-editor-drawer.component';
import { DepartmentsComponent } from './departments/departments.component';
import { DepartmentEditorComponent } from './departments/department-editor/department-editor.component';
import { DepartmentEditorDrawerComponent } from './departments/department-editor-drawer/department-editor-drawer.component';
import { LegalEntitySelectorComponent } from './selection/legal-entity-selector/legal-entity-selector.component';
import { NoAccessPageComponent } from './no-access-page/no-access-page.component';
import { NotFoundPageComponent } from './not-found-page/not-found-page.component';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { AssetUnassignmentComponent } from './assets/asset-unassignment/asset-unassignment.component';

const icons: IconDefinition[] = [ 
  DeleteFill, 
  LogoutOutline, 
  MenuUnfoldOutline, 
  MenuFoldOutline ,
  PlusCircleFill,
  PlusCircleOutline,
  SearchOutline,
  FilterFill,
  FileAddOutline,
  FileAddFill,
  FilePdfFill,
  FilePdfOutline,
  EditFill,
  EditOutline,
  EditTwoTone,
  InfoCircleFill,
  InfoCircleOutline,
  ArrowLeftOutline,
  UserOutline,
  UserAddOutline,
  UserDeleteOutline,
  PlusSquareFill,
  PlusSquareOutline,
  PlusSquareTwoTone,
  MinusSquareFill,
  MinusSquareTwoTone,
  MinusSquareOutline,
  EnvironmentTwoTone,
  PlusCircleTwoTone,
  PlusOutline,
  BarcodeOutline,
  CheckOutline,
  CheckCircleFill,
  CheckCircleTwoTone,
  CheckCircleOutline,
  CheckSquareFill,
  CheckSquareOutline,
  CheckSquareTwoTone,
  InboxOutline
];



@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    AssetsComponent,
    FoundAssetsComponent,
    FoundAssetDetailsComponent,
    ErrorPageComponent,
    NoAccessPageComponent,
    NotFoundPageComponent,
    LocationsComponent,
    EmployeesComponent,
    SuppliersComponent,
    DepartmentsComponent,
    DepartmentEditorComponent,
    DepartmentEditorDrawerComponent,
    LegalEntitiesComponent,
    LegalEntityEditorComponent,
    LegalEntityEditorDrawerComponent,
    AssetDetailsComponent,
    EmployeeDetailsComponent,
    StopClickPropagationDirective,
    LocationDetailsComponent,
    InventoryComponent,
    SizeDetectorComponent,
    InventoryAssetsComponent,
    AssetEditorComponent,
    AssetUnassignmentComponent,
    InventoryFoundAssetEditorComponent,
    DocumentsNeeded,
    NotSelectedItems,
    SortDocuments,
    ConflictingAssets,
    SameBarCode,
    SameBarCodeFoundAssets,
    AssetListComponent,
    UserListComponent,
    SelectionComponent,
    InventoryListComponent,
    InventoryCreateComponent,
    UsersSelectorComponent,
    LegalEntitySelectorComponent,
    DepartmentSelectorComponent,
    EmployeeSelectorComponent,
    LocationSelectorComponent,
    AssetGroupSelectorComponent,
    InventoryDetailsComponent,
    ExcelUploaderComponent,
    AdminPageComponent,
    SupplierEditorComponent,
    SupplierEditorDrawerComponent,
    DiscardRecommendationsComponent
  ],
  imports: [
    BrowserModule,
    LayoutModule,
    NzPopoverModule,
    NzStepsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    IconsProviderModule,
    NgZorroAntdModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    OrderModule,
    NzButtonModule,
    NzTableModule,
    NzMessageModule,
    ZXingScannerModule,
    NzIconModule,
    NzCollapseModule,
    AutonumericModule.forRoot()
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: NZ_ICONS, useValue: icons },
    {
      provide: LOCALE_ID,
      useValue: 'hr-HR' // 'de-DE' for Germany, 'fr-FR' for France ...
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
